// frontend/src/pages/admin/ServiceTemplatesManagement.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalAdminStyles.css";
import PriceTiersEditor from "../../components/PriceTiersEditor";

const ServiceTemplatesManagement = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterBusinessType, setFilterBusinessType] = useState("all");
  const [filterActive, setFilterActive] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch business types and service categories from Platform Settings
  const [businessTypes, setBusinessTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    businessTypes: [],
    applicableTo: [],
    suggestedPrice: "",
    pricingType: "fixed",
    maxPrice: "",
    hourlyRate: "",
    maxHoursPerBooking: 8,
    priceTiers: [],
    requiresConsultation: false,
    suggestedDuration: 30,
    active: true,
  });

  const pricingTypes = [
    { value: "fixed", label: "Fixed Price" },
    { value: "hourly", label: "Hourly (rate x hours)" },
    { value: "range", label: "Options (customer picks one)" },
    // From / Variable stay selectable here so existing templates that already
    // use them remain editable. They are not yet offered in the service editors
    // customers book from, pending the quote flow.
    { value: "from", label: "From (Starting Price)" },
    { value: "variable", label: "Variable (Consultation Required)" },
  ];

  // Fetch business types and service categories from Platform Settings
  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      setLoadingMetadata(true);
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch business types (business categories)
      const businessTypesResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories`,
        { headers },
      );

      // API returns { types: [...] } not { businessCategories: [...] }
      const types = (
        businessTypesResponse.data.types ||
        businessTypesResponse.data.businessCategories ||
        []
      ).map((bt) => ({
        value: bt.value,
        label: bt.label,
      }));
      setBusinessTypes(types);

      // Fetch all service categories by passing all business types
      if (types.length > 0) {
        const businessCategoriesParam = types.map((t) => t.value).join(",");
        const categoriesResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
          { headers },
        );

        // Deduplicate categories (same category might appear for multiple business types)
        const categoryMap = new Map();
        (categoriesResponse.data.categories || []).forEach((cat) => {
          if (!categoryMap.has(cat.value)) {
            categoryMap.set(cat.value, {
              value: cat.value,
              label: cat.label,
            });
          }
        });

        const categoriesArray = Array.from(categoryMap.values());
        setServiceCategories(categoriesArray);
      } else {
      }
    } catch (error) {
      // Fallback to empty arrays if fetch fails
      setBusinessTypes([]);
      setServiceCategories([]);
    } finally {
      setLoadingMetadata(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-templates`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      alert("Failed to fetch service templates");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      businessTypes: [],
      applicableTo: [],
      suggestedPrice: "",
      pricingType: "fixed",
      maxPrice: "",
      hourlyRate: "",
      maxHoursPerBooking: 8,
      priceTiers: [],
      requiresConsultation: false,
      suggestedDuration: 30,
      active: true,
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template._id);
    setFormData({
      name: template.name,
      description: template.description || "",
      category: template.category,
      businessTypes: template.businessTypes || [],
      applicableTo: template.applicableTo || [],
      suggestedPrice: template.suggestedPrice || "",
      pricingType: template.pricingType || "fixed",
      maxPrice: template.maxPrice || "",
      hourlyRate: template.hourlyRate || "",
      maxHoursPerBooking: template.maxHoursPerBooking || 8,
      priceTiers: Array.isArray(template.priceTiers)
        ? template.priceTiers.map((tier) => ({
            label: tier.label || "",
            price: tier.price ?? "",
            durationMinutes: tier.durationMinutes ?? "",
          }))
        : [],
      requiresConsultation: template.requiresConsultation || false,
      suggestedDuration: template.suggestedDuration || 30,
      active: template.active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.name ||
      !formData.category ||
      (formData.pricingType !== "hourly" &&
        formData.pricingType !== "range" &&
        !formData.suggestedDuration)
    ) {
      alert("Please fill in name, category, and suggested duration");
      return;
    }

    if (
      formData.pricingType !== "hourly" &&
      formData.pricingType !== "range" &&
      formData.suggestedDuration < 5
    ) {
      alert("Suggested duration must be at least 5 minutes");
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const url = editingTemplate
        ? `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-templates/${editingTemplate}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-templates`;

      const method = editingTemplate ? "put" : "post";

      // Clean up data
      const payload = {
        ...formData,
        suggestedPrice: formData.suggestedPrice
          ? parseFloat(formData.suggestedPrice)
          : null,
        maxPrice: formData.maxPrice ? parseFloat(formData.maxPrice) : null,
        hourlyRate: formData.hourlyRate
          ? parseFloat(formData.hourlyRate)
          : null,
        suggestedDuration: parseInt(formData.suggestedDuration),
      };

      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          payload.hourlyRate = rate;
          payload.suggestedPrice = rate;
        }
        payload.maxHoursPerBooking = parseInt(
          formData.maxHoursPerBooking || 8,
          10,
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        payload.suggestedDuration = 60;
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          alert(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          alert("Give every option a duration of at least 5 minutes.");
          return;
        }

        const tierPrices = usableTiers.map((t) => t.price);
        payload.priceTiers = usableTiers;
        payload.suggestedPrice = Math.min(...tierPrices);
        payload.maxPrice = Math.max(...tierPrices);
        // Duration field is hidden for range - use the longest option length.
        payload.suggestedDuration = Math.max(
          ...usableTiers.map((t) => Number(t.durationMinutes) || 0),
        );
      }

      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setShowModal(false);
      fetchTemplates();
      alert(
        `Service template ${editingTemplate ? "updated" : "created"} successfully!`,
      );
    } catch (error) {
      alert(error.response?.data?.error || "Failed to save service template");
    }
  };

  const handleDelete = async (templateId) => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate this template? It will no longer be visible to users.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchTemplates();
      alert("Service template deactivated successfully!");
    } catch (error) {
      alert(error.response?.data?.error || "Failed to delete template");
    }
  };

  const toggleActive = async (template) => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/service-templates/${template._id}`,
        { active: !template.active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchTemplates();
    } catch (error) {
      alert("Failed to toggle template status");
    }
  };

  const handleBusinessTypeToggle = (type) => {
    setFormData((prev) => ({
      ...prev,
      businessTypes: prev.businessTypes.includes(type)
        ? prev.businessTypes.filter((t) => t !== type)
        : [...prev.businessTypes, type],
    }));
  };

  const filteredTemplates = templates.filter((template) => {
    if (filterActive !== "all") {
      const isActive = template.active !== false;
      if (filterActive === "active" && !isActive) return false;
      if (filterActive === "inactive" && isActive) return false;
    }

    if (filterCategory !== "all" && template.category !== filterCategory) {
      return false;
    }

    if (filterBusinessType !== "all") {
      if (!template.businessTypes || template.businessTypes.length === 0) {
        // Template available to all business types
        return true;
      }
      if (!template.businessTypes.includes(filterBusinessType)) {
        return false;
      }
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(search) ||
        (template.description &&
          template.description.toLowerCase().includes(search))
      );
    }

    return true;
  });

  if (loading || loadingMetadata) {
    return (
      <PortalAdminLayout>
        <div className="portal-page">
          <div className="portal-loading">
            {loading
              ? "Loading service templates..."
              : "Loading platform metadata..."}
          </div>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        className="portal-page"
        style={{ background: "#f8fafc", minHeight: "100vh", padding: "30px" }}
      >
        {/* Enhanced Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "16px",
            padding: "40px",
            marginBottom: "30px",
            boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h1
                style={{
                  color: "#fff",
                  margin: "0 0 10px 0",
                  fontSize: "32px",
                  fontWeight: "700",
                }}
              >
                ✨ Service Templates
              </h1>
              <p
                style={{
                  color: "rgba(255,255,255,0.9)",
                  margin: 0,
                  fontSize: "16px",
                }}
              >
                Manage global service templates for all professionals
              </p>
            </div>
            <button
              onClick={openAddModal}
              disabled={loadingMetadata}
              style={{
                background: loadingMetadata ? "#e5e7eb" : "#fff",
                color: loadingMetadata ? "#9ca3af" : "#667eea",
                border: "none",
                padding: "14px 28px",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: loadingMetadata ? "not-allowed" : "pointer",
                boxShadow: loadingMetadata
                  ? "none"
                  : "0 4px 15px rgba(0,0,0,0.1)",
                transition: "all 0.3s",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: loadingMetadata ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loadingMetadata) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingMetadata) {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)";
                }
              }}
            >
              <span style={{ fontSize: "18px" }}>➕</span>{" "}
              {loadingMetadata ? "Loading..." : "Add Template"}
            </button>
          </div>
        </div>

        {/* Enhanced Stats Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "16px",
              padding: "24px",
              color: "#fff",
              boxShadow: "0 4px 20px rgba(102, 126, 234, 0.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Total Templates
              </div>
              <div style={{ fontSize: "36px", fontWeight: "700" }}>
                {templates.length}
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                fontSize: "80px",
                opacity: 0.15,
              }}
            >
              📋
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              borderRadius: "16px",
              padding: "24px",
              color: "#fff",
              boxShadow: "0 4px 20px rgba(17, 153, 142, 0.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Active Templates
              </div>
              <div style={{ fontSize: "36px", fontWeight: "700" }}>
                {templates.filter((t) => t.active !== false).length}
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                fontSize: "80px",
                opacity: 0.15,
              }}
            >
              ✅
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: "16px",
              padding: "24px",
              color: "#fff",
              boxShadow: "0 4px 20px rgba(240, 147, 251, 0.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Total Usage
              </div>
              <div style={{ fontSize: "36px", fontWeight: "700" }}>
                {templates.reduce((sum, t) => sum + (t.usageCount || 0), 0)}
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                fontSize: "80px",
                opacity: 0.15,
              }}
            >
              📊
            </div>
          </div>

          <div
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              borderRadius: "16px",
              padding: "24px",
              color: "#fff",
              boxShadow: "0 4px 20px rgba(79, 172, 254, 0.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Filtered Results
              </div>
              <div style={{ fontSize: "36px", fontWeight: "700" }}>
                {filteredTemplates.length}
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                right: "-10px",
                top: "-10px",
                fontSize: "80px",
                opacity: 0.15,
              }}
            >
              🔍
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 2px 15px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                }}
              >
                🔍 Search
              </label>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                }}
              >
                📁 Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "14px",
                  background: "#fff",
                  cursor: "pointer",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="all">All Categories</option>
                {serviceCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                }}
              >
                🏢 Business Type
              </label>
              <select
                value={filterBusinessType}
                onChange={(e) => setFilterBusinessType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "14px",
                  background: "#fff",
                  cursor: "pointer",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="all">All Business Types</option>
                {businessTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#4b5563",
                  marginBottom: "8px",
                }}
              >
                ⚡ Status
              </label>
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "10px",
                  fontSize: "14px",
                  background: "#fff",
                  cursor: "pointer",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Enhanced Templates Table */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 2px 15px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background:
                      "linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%)",
                  }}
                >
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Category
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Business Types
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Price
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "left",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Duration
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Usage
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "18px 20px",
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#374151",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: "#9ca3af",
                      }}
                    >
                      <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                        🔍
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          marginBottom: "8px",
                        }}
                      >
                        No templates found
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        {templates.length > 0
                          ? "Try adjusting your filters."
                          : "Create your first template to get started!"}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTemplates.map((template, index) => (
                    <tr
                      key={template._id}
                      style={{
                        borderTop: "1px solid #f3f4f6",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                    >
                      <td style={{ padding: "20px" }}>
                        <div
                          style={{
                            fontWeight: "600",
                            color: "#111827",
                            fontSize: "14px",
                            marginBottom: "4px",
                          }}
                        >
                          {template.name}
                        </div>
                        {template.description && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              lineHeight: "1.4",
                            }}
                          >
                            {template.description.substring(0, 60)}
                            {template.description.length > 60 && "..."}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "20px" }}>
                        <span
                          style={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "#fff",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "inline-block",
                          }}
                        >
                          {serviceCategories.find(
                            (c) => c.value === template.category,
                          )?.label || template.category}
                        </span>
                      </td>
                      <td style={{ padding: "20px" }}>
                        {template.businessTypes &&
                        template.businessTypes.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                            }}
                          >
                            {template.businessTypes.slice(0, 2).map((bt, i) => (
                              <span
                                key={i}
                                style={{
                                  background: "#e0e7ff",
                                  color: "#4f46e5",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                }}
                              >
                                {businessTypes.find((b) => b.value === bt)
                                  ?.label || bt}
                              </span>
                            ))}
                            {template.businessTypes.length > 2 && (
                              <span
                                style={{
                                  background: "#f3f4f6",
                                  color: "#6b7280",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                }}
                              >
                                +{template.businessTypes.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              fontStyle: "italic",
                            }}
                          >
                            All Types
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "20px" }}>
                        {template.suggestedPrice ? (
                          <span
                            style={{
                              color: "#059669",
                              fontWeight: "600",
                              fontSize: "14px",
                              background: "#d1fae5",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              display: "inline-block",
                            }}
                          >
                            {template.pricingType === "from" && "From "}$
                            {template.suggestedPrice}
                            {template.pricingType === "range" &&
                              template.maxPrice &&
                              `-$${template.maxPrice}`}
                            {template.pricingType === "hourly" && "/hr"}
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "20px" }}>
                        <span
                          style={{
                            background: "#fef3c7",
                            color: "#92400e",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "inline-block",
                          }}
                        >
                          {template.suggestedDuration} min
                        </span>
                      </td>
                      <td style={{ padding: "20px", textAlign: "center" }}>
                        <span
                          style={{
                            background:
                              "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                            color: "#fff",
                            padding: "6px 16px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            fontWeight: "700",
                            display: "inline-block",
                            minWidth: "40px",
                          }}
                        >
                          {template.usageCount || 0}
                        </span>
                      </td>
                      <td style={{ padding: "20px", textAlign: "center" }}>
                        <button
                          onClick={() => toggleActive(template)}
                          style={{
                            background:
                              template.active !== false
                                ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                                : "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)",
                            color: "#fff",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            minWidth: "80px",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          {template.active !== false ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td style={{ padding: "20px" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() => handleEdit(template)}
                            style={{
                              background: "#eef2ff",
                              border: "none",
                              padding: "10px 14px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontSize: "16px",
                              transition: "all 0.2s",
                            }}
                            title="Edit"
                            onMouseEnter={(e) => {
                              e.target.style.background = "#c7d2fe";
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "#eef2ff";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(template._id)}
                            style={{
                              background: "#fee2e2",
                              border: "none",
                              padding: "10px 14px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontSize: "16px",
                              transition: "all 0.2s",
                            }}
                            title="Deactivate"
                            onMouseEnter={(e) => {
                              e.target.style.background = "#fecaca";
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "#fee2e2";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Enhanced Add/Edit Modal */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "20px",
                maxWidth: "700px",
                width: "100%",
                maxHeight: "90vh",
                overflow: "hidden",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  padding: "24px 30px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h2
                  style={{
                    color: "#fff",
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: "700",
                  }}
                >
                  {editingTemplate ? "✏️ Edit Template" : "➕ Add Template"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    color: "#fff",
                    fontSize: "24px",
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                  }
                >
                  ✕
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "30px",
                    overflowY: "auto",
                    flex: 1,
                  }}
                >
                  {/* Name */}
                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Template Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      maxLength={100}
                      placeholder="e.g., Silk Press, Box Braids, Gel Manicure"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "all 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#667eea";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(102, 126, 234, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: "24px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      maxLength={500}
                      rows={3}
                      placeholder="Describe the service..."
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #e5e7eb",
                        borderRadius: "10px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "all 0.2s",
                        fontFamily: "inherit",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#667eea";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(102, 126, 234, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      {formData.description.length}/500 characters
                    </div>
                  </div>

                  {/* Category and Duration Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "8px",
                        }}
                      >
                        Category <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                        required
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "10px",
                          fontSize: "15px",
                          background: "#fff",
                          cursor: "pointer",
                          outline: "none",
                          transition: "all 0.2s",
                          boxSizing: "border-box",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#667eea";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(102, 126, 234, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <option value="">
                          {loadingMetadata
                            ? "Loading categories..."
                            : "Select category"}
                        </option>
                        {serviceCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.pricingType !== "hourly" &&
                      formData.pricingType !== "range" && (
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "8px",
                        }}
                      >
                        Duration (min){" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.suggestedDuration}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            suggestedDuration: e.target.value,
                          })
                        }
                        required
                        min={5}
                        step={5}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #e5e7eb",
                          borderRadius: "10px",
                          fontSize: "15px",
                          outline: "none",
                          transition: "all 0.2s",
                          boxSizing: "border-box",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#667eea";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(102, 126, 234, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </div>
                    )}
                  </div>

                  {/* Pricing Section */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "24px",
                      border: "1px solid #86efac",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 16px 0",
                        fontSize: "16px",
                        color: "#166534",
                        fontWeight: "600",
                      }}
                    >
                      💰 Pricing
                    </h3>

                    <div style={{ marginBottom: "16px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "8px",
                        }}
                      >
                        Pricing Type
                      </label>
                      <select
                        value={formData.pricingType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricingType: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #bbf7d0",
                          borderRadius: "10px",
                          fontSize: "15px",
                          background: "#fff",
                          cursor: "pointer",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      >
                        {pricingTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.pricingType !== "variable" && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            formData.pricingType === "range" ||
                            formData.pricingType === "hourly"
                              ? "1fr 1fr"
                              : "1fr",
                          gap: "16px",
                        }}
                      >
                        {formData.pricingType !== "range" &&
                          formData.pricingType !== "hourly" && (
                        <div>
                          <label
                            style={{
                              display: "block",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#374151",
                              marginBottom: "8px",
                            }}
                          >
                            {formData.pricingType === "hourly"
                              ? "Base Price ($)"
                              : "Suggested Price ($)"}
                          </label>
                          <input
                            type="number"
                            value={formData.suggestedPrice}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                suggestedPrice: e.target.value,
                              })
                            }
                            min={0}
                            step={0.01}
                            placeholder="Optional"
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              border: "2px solid #bbf7d0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        )}

                        {formData.pricingType === "hourly" && (
                          <div>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#374151",
                                marginBottom: "8px",
                              }}
                            >
                              Hourly Rate ($)
                            </label>
                            <input
                              type="number"
                              value={formData.hourlyRate}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  hourlyRate: e.target.value,
                                })
                              }
                              min={0}
                              step={0.01}
                              style={{
                                width: "100%",
                                padding: "12px 16px",
                                border: "2px solid #bbf7d0",
                                borderRadius: "10px",
                                fontSize: "15px",
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#0369a1",
                                marginTop: "6px",
                              }}
                            >
                              The appointment length is the number of hours the
                              customer books (hours x 60 minutes).
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Hourly details - Only for hourly pricing */}
                    {formData.pricingType === "hourly" && (
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          Max hours per booking
                        </label>
                        <input
                          type="number"
                          value={formData.maxHoursPerBooking}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              maxHoursPerBooking: e.target.value,
                            })
                          }
                          min={1}
                          max={24}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "2px solid #bbf7d0",
                            borderRadius: "10px",
                            fontSize: "15px",
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#0369a1",
                            marginTop: "6px",
                          }}
                        >
                          Customers pick how many hours they need. Total price =
                          hourly rate x hours, and the appointment length scales
                          the same way.
                        </div>
                      </div>
                    )}

                    {/* Price options - Only for range pricing (customer picks one) */}
                    {formData.pricingType === "range" && (
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          Price options
                        </label>
                        <PriceTiersEditor
                          tiers={formData.priceTiers || []}
                          onChange={(nextTiers) =>
                            setFormData((prev) => ({
                              ...prev,
                              priceTiers: nextTiers,
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Business Types Section */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "24px",
                      border: "1px solid #93c5fd",
                    }}
                  >
                    <h3
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "16px",
                        color: "#1e40af",
                        fontWeight: "600",
                      }}
                    >
                      🏢 Business Types
                    </h3>
                    <p
                      style={{
                        margin: "0 0 16px 0",
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      Leave empty to show for all business types
                    </p>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: "12px",
                      }}
                    >
                      {businessTypes.map((type) => (
                        <label
                          key={type.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 12px",
                            background: formData.businessTypes.includes(
                              type.value,
                            )
                              ? "#dbeafe"
                              : "#fff",
                            border: `2px solid ${formData.businessTypes.includes(type.value) ? "#3b82f6" : "#e5e7eb"}`,
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: formData.businessTypes.includes(
                              type.value,
                            )
                              ? "600"
                              : "400",
                            color: formData.businessTypes.includes(type.value)
                              ? "#1e40af"
                              : "#6b7280",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!formData.businessTypes.includes(type.value)) {
                              e.currentTarget.style.borderColor = "#93c5fd";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!formData.businessTypes.includes(type.value)) {
                              e.currentTarget.style.borderColor = "#e5e7eb";
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.businessTypes.includes(
                              type.value,
                            )}
                            onChange={() =>
                              handleBusinessTypeToggle(type.value)
                            }
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              accentColor: "#3b82f6",
                            }}
                          />
                          {type.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Active Status */}
                  <div
                    style={{
                      background: "#f9fafb",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        cursor: "pointer",
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) =>
                          setFormData({ ...formData, active: e.target.checked })
                        }
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#10b981",
                        }}
                      />
                      <span>✅ Active (visible to users)</span>
                    </label>
                  </div>
                </div>

                {/* Modal Footer */}
                <div
                  style={{
                    padding: "20px 30px",
                    borderTop: "1px solid #e5e7eb",
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    background: "#f9fafb",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: "12px 24px",
                      border: "2px solid #e5e7eb",
                      background: "#fff",
                      borderRadius: "10px",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#f3f4f6";
                      e.target.style.borderColor = "#d1d5db";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#fff";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: "12px 24px",
                      border: "none",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "10px",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#fff",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 16px rgba(102, 126, 234, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(102, 126, 234, 0.3)";
                    }}
                  >
                    {editingTemplate
                      ? "✅ Update Template"
                      : "➕ Create Template"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default ServiceTemplatesManagement;
